// eslint-disable-next-line no-restricted-imports
import * as moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
// eslint-disable-next-line no-restricted-imports
import type { MomentInput } from 'moment';

import { useInterval } from '../../../hooks';

export const ONE_MINUTE = 60 * 1000;

/**
 * Hook which takes a "date" (ie: any MomentInput) and ensures that
 * the `fromNow` is updated every minute. This ensures that dates
 * displayed fromNow in the UI in long-running sessions will always
 * be displaying up-to-date/accurate fromNow info.
 *
 * @param {MomentInput} date - Could be a string or any valid moment input.
 * @param {number} [updateInterval] - If you wanted to customize how often this is updated.
 * @example
 * const MyComponent = ({ updatedAt }) => {
 *   const updatedAtFromNow = useFromNow(updatedAt);
 *   return (
 *     <div>
 *       Updated {updatedAtFromNow}
 *     </div>
 *   );
 * };
 */
const useFromNow = (date: MomentInput, updateInterval = ONE_MINUTE): string => {
  const [displayDate, setDisplayDate] = useState<string>(moment(date).fromNow());

  //  Handles actually updating the date, but only if it's actually changed.
  const updateDate = useCallback(() => {
    const nextDisplayDate = moment(date).fromNow();
    if (displayDate !== nextDisplayDate) {
      setDisplayDate(nextDisplayDate);
    }
  }, [date, displayDate]);

  //  Update the date each minute.
  useInterval(updateDate, updateInterval);

  //  Handle cases where the parent component has updated the incoming date.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Including `updateDate` cause runaway state updates.
  useEffect(updateDate, [date]);

  return displayDate;
};

export default useFromNow;
